import React from 'react';
import { ContentBlock } from '../../types/content';
import { AlertTriangle, Lightbulb, ListOrdered, Quote } from 'lucide-react';

const ContentBlocks: React.FC<{ blocks: ContentBlock[]; compact?: boolean }> = ({ blocks, compact }) => {
  return (
    <div className="space-y-6">
      {blocks
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((block, index) => (
          <Block key={block.id ?? index} block={block} compact={compact} />
        ))}
    </div>
  );
};

const Block: React.FC<{ block: ContentBlock; compact?: boolean }> = ({ block, compact }) => {
  const type = block.type.toLowerCase();
  const text = block.content ?? '';
  const bodyClass = compact ? 'text-sm text-gray-700' : 'text-gray-800 leading-8 text-lg';

  if (type === 'heading') {
    return <h3 className="text-2xl font-semibold text-gray-900 tracking-tight">{text}</h3>;
  }

  if (type === 'code') {
    return (
      <figure className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-xs text-gray-400">
          <span>{block.language || 'code'}</span>
        </div>
        <pre className="p-4 overflow-x-auto text-sm leading-relaxed text-gray-100">
          <code>{text}</code>
        </pre>
      </figure>
    );
  }

  if (type === 'image' && block.media?.url) {
    return (
      <figure className="space-y-2">
        <img src={block.media.url} alt={block.altText || block.media.altText || ''} className="w-full rounded-xl border border-gray-200" />
        {(block.caption || block.media.caption) && (
          <figcaption className="text-sm text-gray-500 text-center">{block.caption || block.media.caption}</figcaption>
        )}
      </figure>
    );
  }

  if (type === 'image') {
    return (
      <figure className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Diagram</p>
        {block.altText && <p className="font-medium text-gray-900">{block.altText}</p>}
        {text && <p className="whitespace-pre-wrap text-gray-700">{text}</p>}
        {block.caption && <figcaption className="text-sm text-gray-500">{block.caption}</figcaption>}
      </figure>
    );
  }

  if (type === 'quote') {
    return (
      <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-700 flex gap-2">
        <Quote className="h-5 w-5 text-primary-400 shrink-0" />
        <p>{text}</p>
      </blockquote>
    );
  }

  if (type === 'warning' || type === 'callout' || type === 'tip') {
    const styles =
      type === 'warning'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : type === 'tip'
          ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
          : 'bg-primary-50 border-primary-200 text-primary-900';
    const Icon = type === 'warning' ? AlertTriangle : Lightbulb;
    return (
      <div className={`rounded-xl border px-4 py-3 flex gap-3 ${styles}`}>
        <Icon className="h-5 w-5 shrink-0 mt-0.5" />
        <p>{text}</p>
      </div>
    );
  }

  if (type === 'table') {
    const rows = text.split('\n').map((row) => row.split('|').map((cell) => cell.trim()).filter(Boolean)).filter((row) => row.length);
    if (rows.length === 0) return null;
    const [header, ...body] = rows;
    return (
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>{header.map((cell) => <th key={cell} className="px-3 py-2 text-left font-semibold text-gray-800">{cell}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, i) => (
              <tr key={i} className="border-t border-gray-100">
                {row.map((cell, j) => <td key={`${i}-${j}`} className="px-3 py-2 text-gray-700">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'bulletlist' || type === 'numberedlist') {
    const items = text.split('\n').filter(Boolean);
    const List = type === 'numberedlist' ? 'ol' : 'ul';
    return (
      <List className={type === 'numberedlist' ? 'list-decimal pl-6 space-y-1' : 'list-disc pl-6 space-y-1'}>
        {items.map((item) => (
          <li key={item} className={compact ? 'text-sm text-gray-700 leading-relaxed' : 'text-gray-700 leading-relaxed'}>{item}</li>
        ))}
      </List>
    );
  }

  if (type === 'step') {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary-700 mb-2">
          <ListOrdered className="h-4 w-4" />
          Step
        </div>
        <p className={compact ? 'text-sm text-gray-700 leading-relaxed' : 'text-gray-800 leading-relaxed'}>{text}</p>
        {block.children && block.children.length > 0 && (
          <div className="mt-4">
            <ContentBlocks blocks={block.children} compact={compact} />
          </div>
        )}
        {block.expectedResult && (
          <p className="mt-3 text-sm text-gray-700"><span className="font-medium">Expected result: </span>{block.expectedResult}</p>
        )}
        {block.notes && (
          <p className="mt-2 text-sm text-gray-600"><span className="font-medium text-gray-800">Notes: </span>{block.notes}</p>
        )}
        {block.commonMistakes && (
          <p className="mt-2 text-sm text-amber-800"><span className="font-medium">Common mistakes: </span>{block.commonMistakes}</p>
        )}
      </section>
    );
  }

  if (type === 'exercise') {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-secondary-50 p-5">
        <h4 className="font-semibold text-secondary-900 mb-2">Hands-on exercise</h4>
        <p className="text-secondary-900 leading-relaxed">{text}</p>
      </section>
    );
  }

  if (type === 'link' && block.url) {
    return (
      <a href={block.url} className="text-primary-600 hover:underline" target="_blank" rel="noreferrer">
        {text || block.url}
      </a>
    );
  }

  if (type === 'video' && block.url) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden border border-gray-200">
        <iframe title={text || 'Video'} src={block.url} className="w-full h-full" allowFullScreen />
      </div>
    );
  }

  return <p className={`${bodyClass} whitespace-pre-wrap`}>{text}</p>;
};

export default ContentBlocks;
